import Image from 'components/Basic/Image/Image';
import useTranslation from 'next-translate/useTranslation';
import { PlayIcon } from 'public/svg/IconsSvg';
import { twJoin } from 'tailwind-merge';
import { ImageType } from 'types/image';
import twMerge from 'utils/twMerge';

type ProductDetailThumbnailsProps = {
    images: ImageType[];
    youtubeVideo: {
        id: string | null;
        image: string;
    };
    productName: string;
    onClick: (index: number) => void;
};

const productDetailThumbnailsItemTwClass =
    'relative block h-14 w-14 cursor-pointer border border-grayMidDarker hover:border-primary lg:duration-200 lg:ease-defaultTransition sm:w-[68px] sm:h-[68px]';

const productDetailGalleryShowMoreTwClass =
    'absolute inset-0 hidden items-center justify-center bg-white bg-opacity-50 text-h6 font-medium text-primary';

export const ProductDetailThumbnails: FC<ProductDetailThumbnailsProps> = ({
    images,
    youtubeVideo,
    productName,
    onClick,
}) => {
    const { t } = useTranslation();
    const maxVisibleItems = {
        mobile: youtubeVideo.id ? 2 : 3,
        desktop: youtubeVideo.id ? 3 : 4,
    } as const;

    return (
        <ul className="mt-2 flex flex-row justify-center gap-4 lg:relative lg:mr-6 lg:mt-0 lg:w-[100px] lg:flex-shrink-0 lg:flex-col">
            {youtubeVideo.id !== null && (
                <li key={youtubeVideo.id} className={productDetailThumbnailsItemTwClass} onClick={() => onClick(0)}>
                    <Image
                        alt={t('Video of ') + productName}
                        className="mix-blend-multiply"
                        height="66"
                        src={youtubeVideo.image}
                        width="66"
                    />
                    <div className="absolute left-[calc(50%-12px)] top-[calc(50%-8px)] flex h-4 w-6 items-center justify-center rounded-md bg-redLight text-white">
                        <PlayIcon className="h-1.5 w-1.5 text-white" />
                    </div>
                </li>
            )}

            {images.map((image, index) => (
                <li
                    key={index}
                    className={twJoin(
                        index > maxVisibleItems.desktop && 'lg:hidden',
                        index > maxVisibleItems.mobile && 'notLg:hidden',
                        productDetailThumbnailsItemTwClass,
                    )}
                    onClick={() => onClick(youtubeVideo.id ? index + 2 : index + 1)}
                >
                    <Image
                        alt={t('Image of ') + productName}
                        className="h-[68px] w-[68px] mix-blend-multiply"
                        height="68"
                        src={image.url}
                        width="68"
                    />
                    <span
                        className={twMerge(
                            productDetailGalleryShowMoreTwClass,
                            images.length > maxVisibleItems.desktop && index === maxVisibleItems.desktop && 'lg:flex',
                        )}
                    >
                        {`+ ${images.length - maxVisibleItems.desktop}`}
                    </span>
                    <span
                        className={twMerge(
                            productDetailGalleryShowMoreTwClass,
                            images.length > maxVisibleItems.mobile && index === maxVisibleItems.mobile && 'notLg:flex',
                        )}
                    >
                        {`+ ${images.length - maxVisibleItems.mobile}`}
                    </span>
                </li>
            ))}
        </ul>
    );
};
